import { getCachedWorkspace, listRegisteredWorkspaces } from './api-client'

declare const __POSTHOG_KEY__: string
// Injected by the esbuild define at bundle time; absent under vitest (which
// imports this transitively via hook.ts) — and analyticsEvent already no-ops
// on an empty key, so tolerating absence is both test- and runtime-safe.
const KEY = typeof __POSTHOG_KEY__ !== 'undefined' ? __POSTHOG_KEY__ : ''
const HOST = 'https://us.i.posthog.com'
const pending = new Set<Promise<void>>()

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// PostHog identity is workspace:<uuid> — the SAME distinct id the API emits
// (topic_write, api_error, …), so CLI events and server events land on one
// person and cross-surface funnels stitch. Callers keep passing the slug they
// have at hand; the uuid comes from the local config (cached at login / first
// /me). A slug the config doesn't know falls back to the legacy
// workspace:<slug> id rather than dropping the event.
export function resolveDistinctId(slugOrId: string): string {
  if (UUID_RE.test(slugOrId)) return `workspace:${slugOrId}`
  try {
    const cached = getCachedWorkspace()
    if (cached.slug === slugOrId && cached.workspaceId) return `workspace:${cached.workspaceId}`
    const entry = listRegisteredWorkspaces().find((w) => w.slug === slugOrId)
    if (entry?.id) return `workspace:${entry.id}`
  } catch {
    /* config unreadable → legacy slug id */
  }
  return `workspace:${slugOrId}`
}

function withTimeout(promise: Promise<unknown>, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, timeoutMs)
    promise.finally(() => {
      clearTimeout(timer)
      resolve()
    })
  })
}

export function sanitizeAnalyticsMessage(message: unknown): string {
  return String(message ?? 'unknown')
    .replace(/\/Users\/[^/\s]+\/[^\s)'"`]+/g, '[path]')
    .replace(/[A-Za-z]:\\Users\\[^\\\s]+\\[^\s)'"`]+/g, '[path]')
    .slice(0, 180)
}

export function analyticsEvent(
  event: string,
  distinctId: string,
  props: Record<string, unknown> = {},
): Promise<void> {
  if (!KEY || !distinctId) return Promise.resolve()
  // Fire and forget — never blocks the command
  const request = fetch(`${HOST}/capture/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      api_key: KEY,
      event,
      distinct_id: resolveDistinctId(distinctId),
      // Keep the human-readable slug on the event — the distinct id is a uuid now.
      properties: { workspace_slug: distinctId, ...props, source: 'cli', $lib: 'driftless-cli' },
    }),
  })
    .then(() => undefined)
    .catch(() => undefined)
  pending.add(request)
  request.finally(() => pending.delete(request))
  return request
}

export function analyticsCommandError(
  command: string,
  distinctId: string | undefined,
  message: unknown,
  meta: Record<string, unknown> = {},
): Promise<void> {
  if (!distinctId) return Promise.resolve()
  // Drop undefined props so PostHog only stores fields we actually have.
  const cleanMeta = Object.fromEntries(Object.entries(meta).filter(([, v]) => v !== undefined))
  return analyticsEvent('cli_command_error', distinctId, {
    command,
    message: sanitizeAnalyticsMessage(message),
    ...cleanMeta,
  })
}

export async function flushAnalytics(timeoutMs = 300): Promise<void> {
  if (pending.size === 0) return
  await withTimeout(Promise.allSettled([...pending]), timeoutMs)
}

export async function flushAndExit(code: number, timeoutMs = 300): Promise<never> {
  await flushAnalytics(timeoutMs)
  process.exit(code)
}
